import {
	CATEGORY_COMMENT_MAP,
	ColorTokenValue,
	COLOR_SUBCATEGORY_COMMENT_MAP,
	DesignSystemToken,
	LengthTokenValue,
	TokenCategory,
	TokenSubcategory,
	TokenValue,
	TYPOGRAPHY_SUBCATEGORY_COMMENT_MAP,
} from '../types';

// Normalize section comment text to category type using formal mapping
function normalizeCategory(comment: string): TokenCategory {
	const normalized = comment.trim();
	// Look up in formal mapping (case-insensitive)
	const category = Object.entries(CATEGORY_COMMENT_MAP).find(
		([key]) => key.toLowerCase() === normalized.toLowerCase()
	)?.[1];

	if (category) {
		return category;
	}

	// Fallback: assign to 'other' if not found
	return 'other';
}

// Normalize inline subcategory comment text to subcategory type using formal mappings
function normalizeSubcategory(category: TokenCategory, comment: string): TokenSubcategory {
	const normalized = comment.trim();

	if (category === 'typography') {
		// Look up in Typography subcategory mapping
		const subcategory = Object.entries(TYPOGRAPHY_SUBCATEGORY_COMMENT_MAP).find(
			([key]) => key.toLowerCase() === normalized.toLowerCase()
		)?.[1];

		return subcategory || normalized; // Return mapped value or original if not found
	} else if (category === 'color') {
		// Look up in Color subcategory mapping
		const subcategory = Object.entries(COLOR_SUBCATEGORY_COMMENT_MAP).find(
			([key]) => key.toLowerCase() === normalized.toLowerCase()
		)?.[1];

		return subcategory || normalized; // Return mapped value or original if not found
	}

	// Other categories: preserve original text for flexibility
	return normalized;
}

// Generate display-friendly name from CSS variable name
function generateDisplayName(cssVariable: string): string {
	// Remove --ds- prefix
	const withoutPrefix = cssVariable.replace(/^--ds-/, '');
	// Split by hyphens and capitalize each word
	const words = withoutPrefix.split('-').map((word) => {
		return word.charAt(0).toUpperCase() + word.slice(1);
	});
	return words.join(' ');
}

// Normalize short-form hex (#RGB/#RGBA) to full-length (#RRGGBB/#RRGGBBAA)
function normalizeHex(hex: string): string {
	// Remove leading # if present for processing
	const withoutHash = hex.startsWith('#') ? hex.slice(1) : hex;

	// If it's already 6 or 8 characters, return as-is
	if (withoutHash.length === 6 || withoutHash.length === 8) {
		return hex.startsWith('#') ? hex : `#${hex}`;
	}

	// If it's 3 or 4 characters (short form), expand by duplicating each nibble
	if (withoutHash.length === 3 || withoutHash.length === 4) {
		const expanded = withoutHash
			.split('')
			.map((char) => char + char)
			.join('');
		return `#${expanded}`;
	}

	// Return original if it doesn't match expected patterns
	return hex;
}

// Parse hex color to RGB - returns safe default on failure instead of throwing
function hexToRgb(hex: string): { r: number; g: number; b: number; a?: number } {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(hex);
	if (!result) {
		// Return safe default instead of throwing
		return { r: 0, g: 0, b: 0 };
	}
	return {
		r: parseInt(result[1], 16),
		g: parseInt(result[2], 16),
		b: parseInt(result[3], 16),
		a: result[4] ? parseInt(result[4], 16) / 255 : undefined,
	};
}

// Parse RGB/RGBA string - returns safe default on failure instead of throwing
function parseRgb(rgbString: string): { r: number; g: number; b: number; a?: number } {
	const match = rgbString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
	if (!match) {
		// Return safe default instead of throwing
		return { r: 0, g: 0, b: 0 };
	}
	// Clamp RGB values to valid range (0-255)
	const r = Math.max(0, Math.min(255, parseInt(match[1], 10)));
	const g = Math.max(0, Math.min(255, parseInt(match[2], 10)));
	const b = Math.max(0, Math.min(255, parseInt(match[3], 10)));
	const a = match[4] ? Math.max(0, Math.min(1, parseFloat(match[4]))) : undefined;
	return { r, g, b, a };
}

// Parse length value (rem, px, em, etc.) - returns safe default on failure instead of throwing
function parseLength(value: string): LengthTokenValue {
	const trimmed = value.trim();
	const match = trimmed.match(/^([\d.]+)(rem|px|em|pt|pc|in|cm|mm)$/);
	if (!match) {
		// Return safe default instead of throwing
		return {
			kind: 'length',
			original: trimmed,
			numericValue: 0,
			unit: 'px',
			px: 0,
		};
	}

	const numericValue = parseFloat(match[1]);
	// Ensure numericValue is a finite number
	if (!isFinite(numericValue)) {
		return {
			kind: 'length',
			original: trimmed,
			numericValue: 0,
			unit: 'px',
			px: 0,
		};
	}

	const unit = match[2] as LengthTokenValue['unit'];

	// Convert to px (assuming 16px = 1rem base)
	let px: number | undefined;
	if (unit === 'rem') {
		px = numericValue * 16;
	} else if (unit === 'px') {
		px = numericValue;
	} else if (unit === 'em') {
		px = numericValue * 16; // Approximate
	}

	return {
		kind: 'length',
		original: trimmed,
		numericValue,
		unit,
		...(unit === 'rem' && { rem: trimmed }),
		...(px !== undefined && { px }),
		...(unit === 'em' && { em: trimmed }),
	};
}

// Detect and parse token value kind from CSS value
function parseTokenValue(value: string, category: TokenCategory): TokenValue {
	const trimmed = value.trim();

	// Handle edge cases: empty strings or whitespace-only
	if (!trimmed || trimmed.length === 0) {
		return {
			kind: 'string',
			original: value,
		};
	}

	// Limit very long strings to prevent potential DoS (max 1000 chars)
	if (trimmed.length > 1000) {
		return {
			kind: 'string',
			original: trimmed.substring(0, 1000),
		};
	}

	try {
		// Color detection
		if (trimmed.startsWith('#')) {
			// Validate hex format before parsing
			if (!/^#?[a-f\d]{3,8}$/i.test(trimmed)) {
				// Malformed hex - fall through to string type
			} else {
				// Normalize short-form hex (#RGB/#RGBA) to full-length (#RRGGBB/#RRGGBBAA)
				const normalizedHex = normalizeHex(trimmed);
				const rgb = hexToRgb(normalizedHex);
				// Ensure RGB values are valid
				if (isFinite(rgb.r) && isFinite(rgb.g) && isFinite(rgb.b)) {
					return {
						kind: 'color',
						original: trimmed,
						hex: normalizedHex,
						rgb,
					};
				}
				// Invalid RGB values - fall through to string type
			}
		}

		if (trimmed.startsWith('rgb') || trimmed.startsWith('rgba')) {
			const rgb = parseRgb(trimmed);
			// Ensure RGB values are valid
			if (isFinite(rgb.r) && isFinite(rgb.g) && isFinite(rgb.b)) {
				// Convert RGB to hex
				const hex = `#${[rgb.r, rgb.g, rgb.b]
					.map((x) => {
						const hex = x.toString(16);
						return hex.length === 1 ? '0' + hex : hex;
					})
					.join('')}`;
				return {
					kind: 'color',
					original: trimmed,
					hex,
					rgb,
				};
			}
			// Invalid RGB values - fall through to string type
		}

		// HSL color detection
		if (trimmed.startsWith('hsl') || trimmed.startsWith('hsla')) {
			const hslMatch = trimmed.match(/hsla?\(([^)]+)\)/);
			if (hslMatch) {
				const values = hslMatch[1].split(',').map((v) => v.trim());
				if (values.length >= 3) {
					const h = parseFloat(values[0]);
					const s = parseFloat(values[1].replace('%', '')) / 100;
					const l = parseFloat(values[2].replace('%', '')) / 100;
					const a = values[3] ? parseFloat(values[3]) : undefined;

					// Validate HSL values are within valid ranges and are finite numbers
					if (
						isFinite(h) &&
						isFinite(s) &&
						isFinite(l) &&
						h >= 0 &&
						h <= 360 &&
						s >= 0 &&
						s <= 1 &&
						l >= 0 &&
						l <= 1 &&
						(a === undefined || (isFinite(a) && a >= 0 && a <= 1))
					) {
						// Convert HSL to RGB
						const c = (1 - Math.abs(2 * l - 1)) * s;
						const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
						const m = l - c / 2;

						let r = 0,
							g = 0,
							b = 0;
						if (h >= 0 && h < 60) {
							r = c;
							g = x;
							b = 0;
						} else if (h >= 60 && h < 120) {
							r = x;
							g = c;
							b = 0;
						} else if (h >= 120 && h < 180) {
							r = 0;
							g = c;
							b = x;
						} else if (h >= 180 && h < 240) {
							r = 0;
							g = x;
							b = c;
						} else if (h >= 240 && h < 300) {
							r = x;
							g = 0;
							b = c;
						} else if (h >= 300 && h < 360) {
							r = c;
							g = 0;
							b = x;
						}

						const rgb = {
							r: Math.round((r + m) * 255),
							g: Math.round((g + m) * 255),
							b: Math.round((b + m) * 255),
							a,
						};

						// Ensure RGB values are valid
						if (isFinite(rgb.r) && isFinite(rgb.g) && isFinite(rgb.b)) {
							const hex = `#${[rgb.r, rgb.g, rgb.b]
								.map((x) => {
									const hex = x.toString(16);
									return hex.length === 1 ? '0' + hex : hex;
								})
								.join('')}`;

							return {
								kind: 'color',
								original: trimmed,
								hex,
								rgb,
								hsl: { h, s, l, a },
							};
						}
					}
					// If validation fails, fall through to string type
				}
			}
		}

		// Shadow detection
		// Allow optional units on first value (e.g., 0px or 0) to match standard CSS box-shadow syntax
		if (/^[\d.]+(px|rem|em)?\s+[\d.]+(px|rem|em)\s+[\d.]+(px|rem|em)/.test(trimmed)) {
			// Extract numeric values first
			const numericParts: number[] = [];
			const parts = trimmed.split(/\s+/);

			// Collect numeric values (offsetX, offsetY, blur, spread)
			for (let i = 0; i < parts.length; i++) {
				const num = parseFloat(parts[i]);
				if (!isNaN(num) && isFinite(num)) {
					numericParts.push(num);
				} else {
					// Found non-numeric part - everything from here is the color
					break;
				}
			}

			// Validate we have at least offsetX, offsetY, blur
			if (numericParts.length < 3) {
				// Fall through to string type
			} else {
				const offsetX = numericParts[0];
				const offsetY = numericParts[1];
				const blur = numericParts[2];
				const spread = numericParts[3];

				// Extract color string - find the last part that looks like a color
				// Look for color patterns: #hex, rgb(...), rgba(...), hsl(...), hsla(...)
				const colorPattern = /(#[\da-f]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\))/i;
				const colorMatch = trimmed.match(colorPattern);
				const colorString = colorMatch ? colorMatch[1] : '';

				let color: ColorTokenValue;
				if (colorString.startsWith('#')) {
					// Normalize short-form hex (#RGB/#RGBA) to full-length (#RRGGBB/#RRGGBBAA)
					const normalizedHex = normalizeHex(colorString);
					const rgb = hexToRgb(normalizedHex);
					color = {
						kind: 'color',
						original: colorString,
						hex: normalizedHex,
						rgb,
					};
				} else if (colorString.startsWith('rgba') || colorString.startsWith('rgb')) {
					const rgb = parseRgb(colorString);
					const hex = `#${[rgb.r, rgb.g, rgb.b]
						.map((x) => {
							const hex = x.toString(16);
							return hex.length === 1 ? '0' + hex : hex;
						})
						.join('')}`;
					color = {
						kind: 'color',
						original: colorString,
						hex,
						rgb,
					};
				} else if (colorString.startsWith('hsl') || colorString.startsWith('hsla')) {
					// Handle HSL colors in shadows
					const hslMatch = colorString.match(/hsla?\(([^)]+)\)/);
					if (hslMatch) {
						const values = hslMatch[1].split(',').map((v) => v.trim());
						if (values.length >= 3) {
							const h = parseFloat(values[0]);
							const s = parseFloat(values[1].replace('%', '')) / 100;
							const l = parseFloat(values[2].replace('%', '')) / 100;
							const a = values[3] ? parseFloat(values[3]) : undefined;

							// Convert HSL to RGB (same logic as HSL parsing below)
							if (
								isFinite(h) &&
								isFinite(s) &&
								isFinite(l) &&
								h >= 0 &&
								h <= 360 &&
								s >= 0 &&
								s <= 1 &&
								l >= 0 &&
								l <= 1
							) {
								const c = (1 - Math.abs(2 * l - 1)) * s;
								const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
								const m = l - c / 2;

								let r = 0,
									g = 0,
									b = 0;
								if (h >= 0 && h < 60) {
									r = c;
									g = x;
									b = 0;
								} else if (h >= 60 && h < 120) {
									r = x;
									g = c;
									b = 0;
								} else if (h >= 120 && h < 180) {
									r = 0;
									g = c;
									b = x;
								} else if (h >= 180 && h < 240) {
									r = 0;
									g = x;
									b = c;
								} else if (h >= 240 && h < 300) {
									r = x;
									g = 0;
									b = c;
								} else if (h >= 300 && h < 360) {
									r = c;
									g = 0;
									b = x;
								}

								const rgb = {
									r: Math.round((r + m) * 255),
									g: Math.round((g + m) * 255),
									b: Math.round((b + m) * 255),
									a,
								};

								const hex = `#${[rgb.r, rgb.g, rgb.b]
									.map((x) => {
										const hex = x.toString(16);
										return hex.length === 1 ? '0' + hex : hex;
									})
									.join('')}`;

								color = {
									kind: 'color',
									original: colorString,
									hex,
									rgb,
								};
							} else {
								// Invalid HSL values - fallback
								color = {
									kind: 'color',
									original: '#000000',
									hex: '#000000',
									rgb: { r: 0, g: 0, b: 0 },
								};
							}
						} else {
							// Invalid HSL format - fallback
							color = {
								kind: 'color',
								original: '#000000',
								hex: '#000000',
								rgb: { r: 0, g: 0, b: 0 },
							};
						}
					} else {
						// Invalid HSL format - fallback
						color = {
							kind: 'color',
							original: '#000000',
							hex: '#000000',
							rgb: { r: 0, g: 0, b: 0 },
						};
					}
				} else {
					// Fallback color
					color = {
						kind: 'color',
						original: colorString || '#000000',
						hex: '#000000',
						rgb: { r: 0, g: 0, b: 0 },
					};
				}

				return {
					kind: 'shadow',
					original: trimmed,
					offsetX,
					offsetY,
					blur,
					spread,
					color,
				};
			}
		}

		// Font family detection (comma-separated list, may span multiple lines)
		// Check if it looks like a font stack (contains commas and font-like names)
		if (trimmed.includes(',')) {
			// More flexible regex for font families
			const fontFamilyPattern = /^[\w\s"'-]+(?:\s*,\s*[\w\s"'-]+)+$/;
			if (fontFamilyPattern.test(trimmed.replace(/\n/g, ' '))) {
				const families = trimmed
					.replace(/\n/g, ' ')
					.split(',')
					.map((f) => f.trim().replace(/^["']|["']$/g, ''));
				return {
					kind: 'fontFamily',
					original: trimmed,
					families,
				};
			}
		}

		// Length detection
		if (/^[\d.]+(rem|px|em|pt|pc|in|cm|mm)$/.test(trimmed)) {
			const lengthValue = parseLength(trimmed);
			// Check if it's a font size (in typography category with fontSizes subcategory)
			// We'll handle this in the main parser based on subcategory
			return lengthValue;
		}

		// Font weight detection
		const fontWeightMatch = trimmed.match(/^(normal|bold|lighter|bolder|\d+)$/);
		if (fontWeightMatch) {
			const isNumeric = /^\d+$/.test(trimmed);
			let numeric: number;
			if (isNumeric) {
				const parsed = parseInt(trimmed, 10);
				// Clamp font weight to valid range (1-1000)
				numeric = isFinite(parsed) ? Math.max(1, Math.min(1000, parsed)) : 400;
			} else {
				numeric = trimmed === 'bold' ? 700 : 400;
			}
			const keyword = !isNumeric
				? (trimmed as 'normal' | 'bold' | 'lighter' | 'bolder')
				: undefined;
			return {
				kind: 'fontWeight',
				original: trimmed,
				numeric,
				keyword,
			};
		}

		// Transition detection
		if (/[\d.]+s\s+(ease|linear|ease-in|ease-out|ease-in-out|cubic-bezier)/.test(trimmed)) {
			// Extract duration (first part before space)
			const durationMatch = trimmed.match(/^([\d.]+s)/);
			const duration = durationMatch ? durationMatch[1] : '0s';

			// Extract timing function - handle both keywords and cubic-bezier() functions
			let timingFunction = 'ease';
			let remaining = trimmed.substring(duration.length).trim();

			// Check if it's a cubic-bezier function - match the complete function including parentheses
			const cubicBezierMatch = remaining.match(/^cubic-bezier\([^)]+\)/);
			if (cubicBezierMatch) {
				const matched = cubicBezierMatch[0];
				// Validate it's a complete function (has opening and closing parens)
				if (matched.includes('(') && matched.endsWith(')')) {
					timingFunction = matched;
					remaining = remaining.substring(matched.length).trim();
				}
				// If malformed, fall back to 'ease' (timingFunction already set)
			} else {
				// Simple keyword (ease, linear, etc.)
				const keywordMatch = remaining.match(/^(ease|linear|ease-in|ease-out|ease-in-out)/);
				if (keywordMatch) {
					timingFunction = keywordMatch[1];
					remaining = remaining.substring(keywordMatch[1].length).trim();
				}
			}

			// Remaining parts are property and delay (optional)
			const remainingParts = remaining.split(/\s+/).filter((p) => p.length > 0);

			return {
				kind: 'transition',
				original: trimmed,
				duration,
				timingFunction, // Always valid: either a complete cubic-bezier() or a keyword, defaults to 'ease'
				property: remainingParts[0],
				delay: remainingParts[1],
			};
		}

		// Opacity detection (0-1 range)
		if (/^[\d.]+$/.test(trimmed)) {
			const num = parseFloat(trimmed);
			// Ensure number is finite
			if (isFinite(num)) {
				if (num >= 0 && num <= 1) {
					return {
						kind: 'opacity',
						original: trimmed,
						value: num,
					};
				}
				// Could be a number or line height
				if (category === 'typography') {
					// Might be line height
					return {
						kind: 'lineHeight',
						original: trimmed,
						numeric: num,
					};
				}
				return {
					kind: 'number',
					original: trimmed,
					value: num,
				};
			}
			// Invalid number - fall through to string type
		}

		// Fallback to string
		return {
			kind: 'string',
			original: trimmed,
		};
	} catch (error) {
		// Catch any unexpected errors and return safe fallback
		// This ensures we never crash, even for completely unexpected input
		return {
			kind: 'string',
			original: trimmed,
		};
	}
}

// Validate token value to ensure it's safe for CSS rendering
function validateTokenValue(tokenValue: TokenValue): TokenValue {
	switch (tokenValue.kind) {
		case 'transition':
			// Ensure timingFunction is complete (not truncated)
			if (
				tokenValue.timingFunction &&
				tokenValue.timingFunction.startsWith('cubic-bezier(') &&
				!tokenValue.timingFunction.endsWith(')')
			) {
				return {
					...tokenValue,
					timingFunction: 'ease',
				};
			}
			// Ensure duration is valid
			if (!tokenValue.duration || !/^[\d.]+s$/.test(tokenValue.duration)) {
				return {
					...tokenValue,
					duration: '0s',
				};
			}
			break;
		case 'shadow':
			// Ensure color exists and is valid
			if (!tokenValue.color || !tokenValue.color.hex) {
				return {
					...tokenValue,
					color: {
						kind: 'color',
						original: '#000000',
						hex: '#000000',
						rgb: { r: 0, g: 0, b: 0 },
					},
				};
			}
			// Ensure numeric values are finite
			if (
				!isFinite(tokenValue.offsetX) ||
				!isFinite(tokenValue.offsetY) ||
				!isFinite(tokenValue.blur)
			) {
				// Fallback to string type if shadow is invalid
				return {
					kind: 'string',
					original: tokenValue.original,
				};
			}
			break;
		case 'color':
			// Ensure hex/rgb values are valid
			if (!tokenValue.hex || !tokenValue.rgb) {
				return {
					kind: 'color',
					original: tokenValue.original,
					hex: '#000000',
					rgb: { r: 0, g: 0, b: 0 },
				};
			}
			break;
		case 'length':
			// Ensure numericValue is finite number
			if (!isFinite(tokenValue.numericValue)) {
				return {
					kind: 'length',
					original: tokenValue.original,
					numericValue: 0,
					unit: 'px',
					px: 0,
				};
			}
			break;
	}
	return tokenValue;
}

// Post-process token values based on category/subcategory context
function postProcessTokenValue(
	value: TokenValue,
	category: TokenCategory,
	subcategory?: string
): TokenValue {
	// If it's a length in typography with fontSizes subcategory, convert to fontSize
	if (value.kind === 'length' && category === 'typography' && subcategory === 'fontSizes') {
		return {
			kind: 'fontSize',
			original: value.original,
			length: value,
		};
	}

	// If it's a length in border-radius category, convert to borderRadius
	if (value.kind === 'length' && category === 'borderRadius') {
		return {
			kind: 'borderRadius',
			original: value.original,
			length: value,
		};
	}

	return value;
}

export function parseTokens(cssString: string): DesignSystemToken[] {
	const tokens: DesignSystemToken[] = [];
	let currentCategory: TokenCategory = 'other';
	let currentSubcategory: TokenSubcategory | undefined;

	// Extract section comments (main categories)
	// Only accepts format with dashes: /* ---------- Colors ---------- */
	const sectionCommentRegex = /\/\*\s*-+\s*([^-]+?)\s*-+\s*\*\//gi;
	const lines = cssString.split('\n');

	let i = 0;
	while (i < lines.length) {
		const line = lines[i];
		const trimmed = line.trim();

		// Check for section comment with dashes (required format)
		const sectionMatch = trimmed.match(sectionCommentRegex);
		if (sectionMatch) {
			const categoryText = sectionMatch[0]
				.replace(/\/\*\s*-+\s*/, '')
				.replace(/\s*-+\s*\*\//, '')
				.trim();
			currentCategory = normalizeCategory(categoryText);
			currentSubcategory = undefined; // Reset subcategory when new category starts
			i++;
			continue;
		}

		// Check for inline subcategory comment (no dashes)
		if (trimmed.startsWith('/*') && trimmed.endsWith('*/')) {
			const subcategoryText = trimmed.replace(/\/\*\s*|\s*\*\//g, '').trim();
			// Skip section comments (already handled above - they have dashes)
			if (!subcategoryText.includes('---')) {
				currentSubcategory = normalizeSubcategory(currentCategory, subcategoryText);
			}
			i++;
			continue;
		}

		// Extract CSS variable - handle single line
		const singleLineMatch = trimmed.match(/--ds-([^:]+):\s*([^;]+);/);
		if (singleLineMatch) {
			const cssVariable = `--ds-${singleLineMatch[1].trim()}`;
			const value = singleLineMatch[2].trim();

			// Parse the value with error handling
			try {
				let tokenValue = parseTokenValue(value, currentCategory);
				tokenValue = postProcessTokenValue(tokenValue, currentCategory, currentSubcategory);
				tokenValue = validateTokenValue(tokenValue);

				const token: DesignSystemToken = {
					id: cssVariable.replace(/^--ds-/, ''),
					name: generateDisplayName(cssVariable),
					cssVariable,
					category: currentCategory,
					subcategory: currentSubcategory,
					value: tokenValue,
				};

				tokens.push(token);
			} catch (error) {
				// Skip this token if parsing fails - continue processing remaining tokens
				console.warn(`Failed to parse token ${cssVariable}:`, error);
			}
			i++;
			continue;
		}

		// Handle multi-line CSS variables (e.g., font families)
		const multiLineStartMatch = trimmed.match(/--ds-([^:]+):\s*(.+)/);
		if (multiLineStartMatch && !trimmed.includes(';')) {
			const cssVariable = `--ds-${multiLineStartMatch[1].trim()}`;
			let value = multiLineStartMatch[2].trim();
			i++;

			// Continue reading lines until we find the semicolon
			while (i < lines.length && !lines[i].includes(';')) {
				value += ' ' + lines[i].trim();
				i++;
			}

			// Get the final part with semicolon
			if (i < lines.length) {
				const lastLine = lines[i];
				const semicolonIndex = lastLine.indexOf(';');
				if (semicolonIndex !== -1) {
					value += ' ' + lastLine.substring(0, semicolonIndex).trim();
				}
			}

			// Parse the value with error handling
			try {
				let tokenValue = parseTokenValue(value, currentCategory);
				tokenValue = postProcessTokenValue(tokenValue, currentCategory, currentSubcategory);
				tokenValue = validateTokenValue(tokenValue);

				const token: DesignSystemToken = {
					id: cssVariable.replace(/^--ds-/, ''),
					name: generateDisplayName(cssVariable),
					cssVariable,
					category: currentCategory,
					subcategory: currentSubcategory,
					value: tokenValue,
				};

				tokens.push(token);
			} catch (error) {
				// Skip this token if parsing fails - continue processing remaining tokens
				console.warn(`Failed to parse token ${cssVariable}:`, error);
			}
		}

		i++;
	}

	return tokens;
}
